import type { AuthErrorScenario } from '~/types/authError';
import { extractAuthErrorCode } from '~/utils/clerkErrorHandler';

/**
 * Auth error scenarios that have a dedicated destination screen.
 *
 * Only these scenarios trigger a redirect to /auth-error. Other classified
 * scenarios (e.g. 'registration-closed', 'unknown') are handled elsewhere.
 */
const REDIRECTABLE_SCENARIOS: readonly AuthErrorScenario[] = ['trial-expired', 'account-suspended'];

const AUTH_ERROR_PATH = '/auth-error';

/**
 * Global QueryCache error gate. Redirects to the auth-error screen when a
 * query fails with a code that maps to a destination scenario.
 *
 * This runs outside the Router tree, so it uses window.location.assign rather
 * than useNavigate. It complements ErrorBoundary (§FE.3.3) by handling the two
 * auth scenarios that have a dedicated screen, leaving other failures to the
 * boundary.
 *
 * @param error - Unknown error thrown by a failed query
 */
export function handleQueryError(error: unknown): void {
  const { scenario, shouldRedirect } = extractAuthErrorCode(error);

  if (!shouldRedirect || !REDIRECTABLE_SCENARIOS.includes(scenario)) {
    return;
  }

  // Loop guard: never redirect when already on the destination screen.
  if (window.location.pathname === AUTH_ERROR_PATH) {
    return;
  }

  window.location.assign(`${AUTH_ERROR_PATH}?type=${scenario}`);
}
